import React, { useMemo } from 'react';

import { InstructionDialogProps } from '~/components/Instructions/components/InstructionDialog';
import { useGetContentStrings } from '~/data/contentStrings';
import { UseDialogStateOutput, useDialogState } from '~/hooks/useDialogState';
import { getCurrentLanguage } from '~/localization';

export type InstructionDialogConfig = {
  title: string;
  contentKey?: string;
  content?: React.ReactNode;
};

export type UseInstructionDialogOutput = {
  isEnabled: boolean;
  dialogState: UseDialogStateOutput;
  dialogProps: InstructionDialogProps;
};

export const useInstructionDialog = (
  props?: InstructionDialogConfig,
): UseInstructionDialogOutput => {
  const currentLang = getCurrentLanguage();
  const { isLoading, data } = useGetContentStrings({
    contentKeys: !!props?.contentKey ? [props?.contentKey] : [],
    langCode: currentLang,
  });

  const {
    isEnabled,
    ...content
  }: { isEnabled: boolean } & Pick<
    InstructionDialogProps,
    'title' | 'richTextContent' | 'children'
  > = useMemo(() => {
    const richTextContent =
      !!props?.contentKey && !isLoading && !!data?.[props?.contentKey]
        ? data?.[props?.contentKey]
        : undefined;

    return {
      isEnabled: !!richTextContent || !!props?.content,
      title: props?.title ?? '',
      richTextContent,
      children: props?.content,
    };
  }, [props, data, isLoading]);

  const dialogState = useDialogState();
  const { isOpen, close } = dialogState;

  return {
    isEnabled,
    dialogState,
    dialogProps: {
      ...content,
      open: isOpen,
      onClose: close,
    },
  };
};
